import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/Merge';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, ButtonGroup, Fab, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { JSONPath } from 'jsonpath-plus';
import { createRef, useEffect, useState } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import { useAuth } from './AuthContext.js';
import ScrollableTableContainer, { MEDICAL_EXPENSES_TABLE_MIN_WIDTH, scrollableTableSx } from './ScrollableTableContainer.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildArraySchemaWarningPath, buildFieldPaths, getFilesWithUnConsolidatedExtractions, getFormatFunction, getJSONPathCompatibleKey, getParseFunction, getUserLocale, hasAnyConsolidatedExtractions, MEDICAL_EXTRACTION_ANALYSIS_IDS, renderTreatmentDates } from './PathUtils.js';
import { respondToUnauthorized } from './sessionUnauthorized.js';
import SourceLink from './SourceLink.js';
import { getSourceJumpPage } from './SourceUtils.js';

const medicalExpenseHeaderCellSx = {
    fontWeight: 600,
    color: 'var(--text-primary)',
    borderBottom: '2px solid var(--border-color)',
    padding: '16px 12px',
};

const medicalExpenseHeaderCellRightSx = {
    ...medicalExpenseHeaderCellSx,
    textAlign: 'right',
};

const medicalExpenseAmountSubHeaderSx = {
    ...medicalExpenseHeaderCellRightSx,
    fontSize: '0.85rem',
};

const medicalExpenseSummaryRows = [
    { field: 'total_charge', label: 'Total Charge', emphasized: true },
    { field: 'total_adjustment', label: 'Adjustments', emphasized: false },
    { field: 'total_insurance_paid', label: 'Insurance Paid', emphasized: false },
    { field: 'total_insurance_balance', label: 'Insurance Balance', emphasized: true },
    { field: 'total_patient_paid', label: 'Patient Paid', emphasized: true },
    { field: 'total_patient_balance', label: 'Patient Balance', emphasized: true },
];

function renderMedicalExpensesGroupedTableHead(sourceColumnLabel) {
    return (
        <>
            <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                <TableCell rowSpan={2} sx={{ ...medicalExpenseHeaderCellSx, minWidth: '150px' }}>
                    Treatment Date
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellSx}>
                    Patient Name
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellSx}>
                    Treatment/Service
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellSx}>
                    Provider/Hospital
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellSx}>
                    Insurance
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellRightSx}>
                    Total Charge
                </TableCell>
                <TableCell rowSpan={2} sx={medicalExpenseHeaderCellRightSx}>
                    Adjustments
                </TableCell>
                <TableCell colSpan={2} align="center" sx={{ ...medicalExpenseHeaderCellSx, borderBottom: '1px solid var(--border-color)' }}>
                    Insurance
                </TableCell>
                <TableCell colSpan={2} align="center" sx={{ ...medicalExpenseHeaderCellSx, borderBottom: '1px solid var(--border-color)' }}>
                    Patient
                </TableCell>
                {sourceColumnLabel && (
                    <TableCell rowSpan={2} sx={medicalExpenseHeaderCellSx}>
                        {sourceColumnLabel}
                    </TableCell>
                )}
            </TableRow>
            <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                <TableCell sx={medicalExpenseAmountSubHeaderSx}>Paid</TableCell>
                <TableCell sx={medicalExpenseAmountSubHeaderSx}>Balance</TableCell>
                <TableCell sx={medicalExpenseAmountSubHeaderSx}>Paid</TableCell>
                <TableCell sx={medicalExpenseAmountSubHeaderSx}>Balance</TableCell>
            </TableRow>
        </>
    );
}

function isPrimaryMedicalExpenseAmountField(fieldName) {
    return fieldName === 'total_charge'
        || fieldName === 'total_insurance_balance'
        || fieldName === 'total_patient_paid'
        || fieldName === 'total_patient_balance';
}

export default function MedicalExpenses({ caseInfo, setCaseInfo, fetchCaseInfoAndUpdateUI, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage, subscriptionOk = true, onSubscriptionRequired }) {
    const { logout } = useAuth();
    const tableRef = createRef();
    const summaryTableRef = createRef();
    const hasConsolidated = hasAnyConsolidatedExtractions(caseInfo, 'medical_reports_canonicalize');
    const [showConsolidatedOnly, setShowConsolidatedOnly] = useState(hasConsolidated);
    const [expandedSourcesLists, setExpandedSourcesLists] = useState({});
    const unconsolidatedFiles = getFilesWithUnConsolidatedExtractions(caseInfo, MEDICAL_EXTRACTION_ANALYSIS_IDS, ['medical_expenses'], 'medical_reports_canonicalize');
    const hasUnconsolidated = unconsolidatedFiles.length > 0;
    const isMedicalRecordsInProgress = caseInfo && caseInfo['documents'] && Object.values(caseInfo['documents']).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('medical_records') || doc['analysis_queue']?.includes('personal_injury_catchall'));
    const isConsolidationInProgress = caseInfo['analysis_queue']?.[0]?.includes('medical_reports_');
    const isProcessingInProgress = caseInfo['analysis_queue']?.length > 0 ||
        Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.length > 0);
    assert('caseId' in caseInfo, 'caseId not found in caseInfo');

    // Helper function to get currency code for a medical expense item
    const getCurrencyCode = (documentKey, itemIndex) => {
        try {
            const currencyPath = `$.documents['${documentKey}'].analysis.medical_expenses[${itemIndex}].currency_code`;
            const results = JSONPath({
                json: caseInfo,
                path: currencyPath,
                resultType: 'value'
            });
            if (results.length === 0) {
                return 'USD';
            }
            const currencyCode = results[0];

            // List of valid ISO 4217 currency codes
            const validCurrencyCodes = [
                'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
                'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
                'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
                'DJF', 'DKK', 'DOP', 'DZD',
                'EGP', 'ERN', 'ETB', 'EUR',
                'FJD', 'FKP',
                'GBP', 'GEL', 'GGP', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
                'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
                'IDR', 'ILS', 'IMP', 'INR', 'IQD', 'IRR', 'ISK',
                'JEP', 'JMD', 'JOD', 'JPY',
                'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
                'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
                'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
                'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
                'OMR',
                'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
                'QAR',
                'RON', 'RSD', 'RUB', 'RWF',
                'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD', 'STN', 'SYP', 'SZL',
                'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TVD', 'TWD', 'TZS',
                'UAH', 'UGX', 'USD', 'UYU', 'UZS',
                'VED', 'VES', 'VND', 'VUV',
                'WST',
                'XAF', 'XCD', 'XDR', 'XOF', 'XPF',
                'YER',
                'ZAR', 'ZMW', 'ZWL'
            ];

            // Check if the currency code is in the valid list
            if (!validCurrencyCodes.includes(currencyCode)) {
                console.warn(`Invalid currency code '${currencyCode}' found, falling back to USD`);
                return 'USD';
            }
            return currencyCode;
        } catch (error) {
            console.error('Error extracting currency code:', error);
            return 'USD';
        }
    };

    const openDocumentSource = (documentKey, page = 1) => {
        setDisplayedDocument({ key: documentKey, page });
    };

    const renderDocumentAmountField = (documentKey, itemIndex, fieldName, medicalExpense) => {
        const emphasized = isPrimaryMedicalExpenseAmountField(fieldName);
        return (
            <TableCell
                key={fieldName}
                sx={{
                    color: emphasized ? 'var(--text-primary)' : 'var(--text-secondary)',
                    borderBottom: '1px solid var(--border-color)',
                    fontWeight: emphasized ? 500 : 400,
                    textAlign: 'right',
                }}
            >
                <EditableFieldWithWarnings
                    onWarningClick={(e) => {
                        e.stopPropagation();
                        openDocumentSource(documentKey, getSourceJumpPage(medicalExpense.source));
                    }}
                    caseInfo={caseInfo}
                    setCaseInfo={setCaseInfo}
                    {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, fieldName)}
                    formatValue={getFormatFunction(fieldName)}
                    parseValue={getParseFunction(fieldName)}
                    callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                    setSnackBarMessage={setSnackBarMessage}
                    style={{ textAlign: 'right', fontWeight: emphasized ? 500 : 400, whiteSpace: 'nowrap' }}
                />
            </TableCell>
        );
    };

    const renderConsolidatedAmountField = (reportIndex, itemIndex, fieldName, medicalExpense, source) => {
        const emphasized = isPrimaryMedicalExpenseAmountField(fieldName);
        return (
            <TableCell
                key={fieldName}
                sx={{
                    color: emphasized ? 'var(--text-primary)' : 'var(--text-secondary)',
                    borderBottom: '1px solid var(--border-color)',
                    fontWeight: emphasized ? 500 : 400,
                    textAlign: 'right',
                }}
            >
                <EditableFieldWithWarnings
                    caseInfo={caseInfo}
                    setCaseInfo={setCaseInfo}
                    {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, fieldName)}
                    formatValue={getFormatFunction(fieldName)}
                    parseValue={getParseFunction(fieldName)}
                    callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                    setSnackBarMessage={setSnackBarMessage}
                    style={{ textAlign: 'right', fontWeight: emphasized ? 500 : 400, whiteSpace: 'nowrap' }}
                    onWarningClick={() => {
                        setDisplayedDocument({
                            key: getJSONPathCompatibleKey(source?.filename || ''),
                            page: getSourceJumpPage(source)
                        });
                    }}
                />
            </TableCell>
        );
    };

    // Auto-switch to consolidated view when consolidation completes
    useEffect(() => {
        if (hasConsolidated) {
            setShowConsolidatedOnly(true);
        }
    }, [hasConsolidated]);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header section with ButtonGroup on left and CopyToClipboardButton on right */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {hasConsolidated && (
                        <ButtonGroup
                            sx={{
                                visibility: hasConsolidated ? "visible" : "hidden",
                                pointerEvents: "auto"
                            }}
                            aria-label="view mode button group"
                            variant="text"
                            size="small"
                            className="selectConsolidatedButtonGroup"
                        >
                            <Button
                                onClick={() => setShowConsolidatedOnly(false)}
                                disabled={!showConsolidatedOnly}
                                key="all"
                                size="small"
                            >
                                All Records
                            </Button>
                            <Button
                                onClick={() => setShowConsolidatedOnly(true)}
                                disabled={showConsolidatedOnly}
                                key="consolidated"
                                size="small"
                            >
                                Consolidated Records
                            </Button>
                        </ButtonGroup>
                    )}
                    {isMedicalRecordsInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Medical Expenses are being extracted...
                            </Typography>
                        </Box>
                    )}
                    {!isMedicalRecordsInProgress && isConsolidationInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Consolidating medical expenses...
                            </Typography>
                        </Box>
                    )}
                    {((hasConsolidated && hasUnconsolidated) || (unconsolidatedFiles.length > 1)) && !isProcessingInProgress && (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--info-color)',
                                }}
                            >
                                {hasConsolidated && hasUnconsolidated
                                    ? `Click Consolidate to incorporate new data extracted from ${unconsolidatedFiles.length} files`
                                    : 'Raw information extracted from files below. Click Consolidate to generate a single, master medical report.'
                                }
                            </Typography>
                            <Fab
                                onClick={async () => {
                                    if (!subscriptionOk) {
                                        onSubscriptionRequired?.();
                                        return;
                                    }
                                    const response = await fetch('/api/case/' + caseInfo['caseId'] + '/analysis_queue', {
                                        method: 'POST',
                                        credentials: 'same-origin',
                                        headers: new Headers({ 'content-type': 'application/json' }),
                                        body: JSON.stringify({
                                            analysis_queue: ['medical_reports_canonicalize'],
                                            item_type: 'consolidation'
                                        })
                                    });
                                    console.log('Consolidate button clicked: ' + response);

                                    if (respondToUnauthorized(response, logout)) {
                                        return;
                                    }

                                    if (response.ok) {
                                        setCaseInfo({ ...caseInfo, analysis_queue: [...caseInfo['analysis_queue'] || [], 'medical_reports_canonicalize'] });
                                        setTimeout(fetchCaseInfoAndUpdateUI, 10000);
                                    }
                                }}
                                variant="extended"
                                size="medium"
                                color="primary"
                                sx={{
                                    top: "-1px",
                                    background: 'var(--button-gradient)',
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    padding: '14px 24px',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: 'none',
                                    ...(!subscriptionOk && {
                                        opacity: 0.45,
                                        boxShadow: 'none',
                                        '&:hover': {
                                            transform: 'none',
                                            boxShadow: 'none',
                                            background: 'var(--button-gradient)',
                                        },
                                        '&:hover::before': {
                                            left: '-100%',
                                        },
                                    }),
                                    ...(subscriptionOk && {
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                            background: 'var(--button-gradient-hover)'
                                        },
                                        '&:active': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                        },
                                        '&:hover::before': {
                                            left: '100%'
                                        },
                                    }),
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        transition: 'left 0.6s'
                                    },
                                }}
                            >
                                <MergeIcon sx={{ mr: 1 }} style={{ transform: 'rotate(90deg)' }} />
                                Consolidate
                            </Fab>
                        </Box>
                    )}
                </Box>
                {!showConsolidatedOnly && <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Expenses" contentType="medical_expenses" />}
            </Box>

            {/* Content section below the header */}
            {showConsolidatedOnly ? consolidatedMedicalExpenses() : allMedicalExpenses()}
        </Box>
    );

    function consolidatedMedicalExpenses() {
        const medicalReports = caseInfo?.analysis?.medical_reports || [];

        if (medicalReports.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        No consolidated medical reports available.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                {medicalReports.map((report, reportIndex) => (
                    <Accordion
                        key={report.uuid || reportIndex}
                        defaultExpanded={true}
                        sx={{
                            backgroundColor: 'var(--background-color)',
                            border: '1px solid var(--border-color)',
                            boxShadow: 'var(--shadow-light)',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            }
                        }}
                    >
                        <AccordionSummary
                            // Make only icon clickable to expand/contract Accordian from https://stackoverflow.com/questions/69358781/material-ui-expand-accordion-by-clicking-the-icon-only
                            sx={{
                                pointerEvents: "none",
                                backgroundColor: 'var(--surface-color)',
                                borderBottom: '1px solid var(--border-color)',
                                height: '64px', // Without this, height of accordian summary changes when expanded or collapsed due to minHeigh rule only applied on expanded by library.
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'var(--surface-color)'
                                }
                            }}
                            expandIcon={<ExpandMoreIcon
                                sx={{
                                    pointerEvents: "auto",
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        color: 'var(--accent-color)',
                                        transform: 'scale(1.1)',
                                    }
                                }}
                            // onClick={onAccordianIconClicked}
                            />}
                            style={{
                                flexDirection: "row-reverse",
                                cursor: "default",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}
                            >
                                Medical Expenses for {report.patient_name || 'Unknown Patient'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                            {/* Summary Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Summary
                                </Typography>
                                <CopyToClipboardButton nodeRef={summaryTableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Expenses Summary" contentType="expenses_summary" />
                            </Box>
                            {renderSummaryTable(report, reportIndex)}

                            {/* Medical Expenses Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Medical Expenses
                                </Typography>
                                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Expenses" contentType="medical_expenses" />
                            </Box>
                            {renderConsolidatedMedicalExpensesTable(report.medical_expenses || [], reportIndex)}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    }

    function allMedicalExpenses() {
        // Get document-based medical expenses (All Records)
        var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
            Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
            [];
        var medicalExpenses = documentKeysToDisplay.map(documentKey => {
            caseInfo['documents'][documentKey]['analysis']?.['medical_expenses']?.forEach((medicalExpense, i) => {
                medicalExpense['id'] = documentKey + i; // Used in UI as keys for use by React framework
                medicalExpense['source_filekey'] = documentKey; // File used by SourceDocumentViewer
            });
            return caseInfo['documents'][documentKey]['analysis']?.['medical_expenses'];
        })
            .filter(medicalExpenseArr => medicalExpenseArr) // the ?. above can cause null entries; filter them
            .reduce((accumulator, medicalExpenseArr) => accumulator.concat(medicalExpenseArr), [])
            .filter(medicalExpense => isValidMedicalExpenseItem(medicalExpense)) // Remove '', 'N/A', null, etc
            .sort((expense1, expense2) =>
                Date.parse(expense1.treatment_start_date || '') -
                Date.parse(expense2.treatment_start_date || ''));

        if (medicalExpenses.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Medical charges will be automatically extracted from uploaded medical bills, invoices, etc and listed here.
                    </Typography>
                </Box>
            )
        }

        var tableBodyRows = medicalExpenses
            .map(medicalExpense => {
                // Find the document key for this expense item
                const documentKey = medicalExpense.source_filekey;
                const docInfo = caseInfo.documents[documentKey];
                const filename = docInfo?.['filename'];
                const itemIndex = caseInfo.documents[documentKey]?.analysis?.medical_expenses?.findIndex(item => item.id === medicalExpense.id) || 0;

                return (
                    <TableRow
                        key={medicalExpense['id']}
                        className={medicalExpense['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}
                    >
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            {renderTreatmentDate(medicalExpense, documentKey, itemIndex)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense.source)
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'patient_name')}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ fontWeight: 500 }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '200px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense.source)
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'treatment')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '150px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense.source)
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'payee')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense['source'])
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'insurance')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500,
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense.source)
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_charge')}
                                formatValue={getFormatFunction('total_charge')}
                                parseValue={getParseFunction('total_charge')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': getSourceJumpPage(medicalExpense.source)
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_adjustment')}
                                formatValue={getFormatFunction('total_adjustment')}
                                parseValue={getParseFunction('total_adjustment')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        {renderDocumentAmountField(documentKey, itemIndex, 'total_insurance_paid', medicalExpense)}
                        {renderDocumentAmountField(documentKey, itemIndex, 'total_insurance_balance', medicalExpense)}
                        {renderDocumentAmountField(documentKey, itemIndex, 'total_patient_paid', medicalExpense)}
                        {renderDocumentAmountField(documentKey, itemIndex, 'total_patient_balance', medicalExpense)}
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <SourceLink
                                caseId={caseInfo?.caseId}
                                fileName={filename}
                                documentKey={medicalExpense['source_filekey']}
                                source={medicalExpense.source}
                                setDisplayedDocument={setDisplayedDocument}
                            />
                        </TableCell>
                    </TableRow>
                );
            });

        return (
            <ScrollableTableContainer>
                <Table
                    ref={tableRef}
                    sx={scrollableTableSx(MEDICAL_EXPENSES_TABLE_MIN_WIDTH)}
                    aria-label="medical expenses table"
                >
                    <TableHead>
                        {renderMedicalExpensesGroupedTableHead('Source Document')}
                    </TableHead>
                    <TableBody>
                        {tableBodyRows}
                    </TableBody>
                </Table>
            </ScrollableTableContainer>
        );
    }

    // Helper functions for consolidated medical expenses (new array schema for needs_review/caveats)
    function buildConsolidatedFieldPaths(reportIndex, dataType, itemIndex, fieldName) {
        const base = `$.analysis.medical_reports[${reportIndex}].${dataType}[${itemIndex}]`;
        return {
            textPath: `${base}.${fieldName}`,
            needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        }
    }

    function buildConsolidatedSingleFieldPaths(reportIndex, dataType, fieldName) {
        const base = `$.analysis.medical_reports[${reportIndex}].${dataType}`;
        return {
            textPath: `${base}.${fieldName}`,
            needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        }
    }

    function renderSummaryTable(report, reportIndex) {
        const summary = report.medical_expenses_summary || {};
        // Get the first source from the first medical expense for warning navigation
        const firstSource = report.medical_expenses?.[0]?.sources?.[0] ||
            report.medical_expenses?.[0]?.source || {};

        return (
            <Table
                ref={summaryTableRef}
                sx={{
                    mb: 4,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            width: '200px'
                        }}>
                            Description
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Details
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {medicalExpenseSummaryRows.map(({ field, label, emphasized }) => (
                        <TableRow key={field}>
                            <TableCell sx={{
                                fontWeight: 500,
                                color: 'var(--text-primary)',
                                borderBottom: '1px solid var(--border-color)',
                                padding: '12px'
                            }}>
                                {label}
                            </TableCell>
                            <TableCell sx={{
                                color: emphasized ? 'var(--text-primary)' : 'var(--text-secondary)',
                                borderBottom: '1px solid var(--border-color)',
                                padding: '12px',
                                textAlign: 'right',
                                fontWeight: emphasized ? 500 : 400
                            }}>
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...buildConsolidatedSingleFieldPaths(reportIndex, 'medical_expenses_summary', field)}
                                    formatValue={getFormatFunction(field)}
                                    parseValue={getParseFunction(field)}
                                    callbackArguments={{ currencyCode: summary.currency_code || 'USD', locale: getUserLocale() }}
                                    setSnackBarMessage={setSnackBarMessage}
                                    style={{ textAlign: 'right', fontWeight: emphasized ? 500 : 400 }}
                                    onWarningClick={() => {
                                        setDisplayedDocument({
                                            'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                            'page': getSourceJumpPage(firstSource)
                                        });
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    function renderConsolidatedMedicalExpensesTable(medicalExpenses, reportIndex) {
        if (!medicalExpenses || medicalExpenses.length === 0) {
            return (
                <Typography sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                    No medical expenses available
                </Typography>
            );
        }

        // Sort medical expenses by treatment date
        const sortedExpenses = [...medicalExpenses].sort((expense1, expense2) =>
            Date.parse(expense1.treatment_start_date || '') -
            Date.parse(expense2.treatment_start_date || '')
        );

        const tableBodyRows = sortedExpenses.map((medicalExpense, itemIndex) => {
            let source = medicalExpense.source || medicalExpense.sources?.[0] || {};
            return (
                <TableRow
                    key={medicalExpense.uuid || itemIndex}
                    sx={{
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderConsolidatedTreatmentDate(medicalExpense, reportIndex, itemIndex)}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'patient_name')}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ fontWeight: 500 }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        maxWidth: '200px'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'treatment')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        maxWidth: '150px'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'payee')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'insurance')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 500,
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_charge')}
                            formatValue={getFormatFunction('total_charge')}
                            parseValue={getParseFunction('total_charge')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_adjustment')}
                            formatValue={getFormatFunction('total_adjustment')}
                            parseValue={getParseFunction('total_adjustment')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': getSourceJumpPage(source)
                                });
                            }}
                        />
                    </TableCell>
                    {renderConsolidatedAmountField(reportIndex, itemIndex, 'total_insurance_paid', medicalExpense, source)}
                    {renderConsolidatedAmountField(reportIndex, itemIndex, 'total_insurance_balance', medicalExpense, source)}
                    {renderConsolidatedAmountField(reportIndex, itemIndex, 'total_patient_paid', medicalExpense, source)}
                    {renderConsolidatedAmountField(reportIndex, itemIndex, 'total_patient_balance', medicalExpense, source)}
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderSourcesList(medicalExpense.sources || [], `consolidated-expense-${reportIndex}-${medicalExpense.uuid || itemIndex}`)}
                    </TableCell>
                </TableRow>
            )
        });

        return (
            <ScrollableTableContainer>
                <Table
                    ref={tableRef}
                    sx={scrollableTableSx(MEDICAL_EXPENSES_TABLE_MIN_WIDTH)}
                    aria-label="consolidated medical expenses table"
                >
                    <TableHead>
                        {renderMedicalExpensesGroupedTableHead('Source Document')}
                    </TableHead>
                    <TableBody>
                        {tableBodyRows}
                    </TableBody>
                </Table>
            </ScrollableTableContainer>
        );
    }

    function renderConsolidatedTreatmentDate(medicalExpense, reportIndex, itemIndex) {
        const baseFieldPath = `$.analysis.medical_reports[${reportIndex}].medical_expenses[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument);
    }

    function renderSourcesList(sources, listKey) {
        if (!sources || sources.length === 0) {
            return 'N/A';
        }
        const isExpanded = expandedSourcesLists[listKey] === true;
        const visibleSources = sources.length > 2 && !isExpanded
            ? sources.slice(0, 2)
            : sources;
        return (
            <Box>
                {visibleSources.map((source, index) => (
                    <Box key={index} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SourceLink
                            caseId={caseInfo?.caseId}
                            fileName={source?.filename}
                            documentKey={getJSONPathCompatibleKey(source?.filename)}
                            source={source}
                            setDisplayedDocument={setDisplayedDocument}
                        />
                    </Box>
                ))}
                {sources.length > 2 && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setExpandedSourcesLists((prev) => ({
                            ...prev,
                            [listKey]: !isExpanded
                        }))}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isExpanded ? 'Show less' : 'See all sources'}
                    </Button>
                )}
            </Box>
        );
    }

    function isValidMedicalExpenseItem(medicalExpense) {
        return medicalExpense['total_charge'];
    }

    function renderTreatmentDate(medicalExpense, documentKey, itemIndex) {
        const baseFieldPath = `$.documents['${documentKey}'].analysis.medical_expenses[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument, documentKey);
    }
}

// TODO: Move to helper
function assert(condition, message) {
    if (!condition) {
        throw new Error(`Invariant failed: ${message || "Condition not met."}`);
    }
}
